import React, { useEffect, useState, useCallback } from "react";
import { Check, X, RefreshCw } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api, errorMessage } from "@/lib/api";

function ApproveDialog({ user, modules, onClose, onConfirm }) {
  const [picked, setPicked] = useState(
    (user.assigned_modules && user.assigned_modules.length > 0)
      ? user.assigned_modules
      : (user.requested_modules || [])
  );
  const toggle = (s) => setPicked((p) => p.includes(s) ? p.filter(x => x !== s) : [...p, s]);
  const allSlugs = modules.map((m) => m.slug);
  const allSelected = allSlugs.every((s) => picked.includes(s));
  const selectAll = () => setPicked(allSelected ? [] : allSlugs);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white border border-border max-w-md w-full p-6 max-h-[90vh] flex flex-col">
        <div className="overline mb-2">Approve user</div>
        <div className="font-display font-black text-xl tracking-tight mb-1">{user.full_name}</div>
        <div className="text-[13px] text-muted-foreground mb-4">{user.email}</div>
        {user.requested_modules?.length > 0 && (
          <div className="mb-5 p-3 border border-primary/30 bg-primary/5 text-[12.5px]">
            <div className="overline text-primary mb-1">User requested</div>
            <div className="font-semibold">
              {user.requested_modules.map((s) => modules.find((m) => m.slug === s)?.name || s).join(", ")}
            </div>
          </div>
        )}
        <div className="flex items-center justify-between mb-3">
          <div className="overline">Assign modules</div>
          <button type="button" onClick={selectAll} data-testid="approve-select-all"
                  className="text-[11.5px] font-semibold text-primary link-underline">
            {allSelected ? "Clear all" : "Select all"}
          </button>
        </div>
        <div className="space-y-2 mb-6 overflow-y-auto pr-1">
          {modules.map((m) => {
            const isReq = user.requested_modules?.includes(m.slug);
            return (
              <label key={m.slug} className={`flex items-center gap-3 p-3 border cursor-pointer ${picked.includes(m.slug) ? "border-primary bg-primary/5" : "border-border"}`}>
                <input data-testid={`assign-module-${m.slug}`} type="checkbox" checked={picked.includes(m.slug)} onChange={() => toggle(m.slug)} className="accent-[#002FA7]" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[13.5px] truncate">{m.name}</div>
                  <div className="text-[11.5px] text-muted-foreground font-mono">{m.slug}</div>
                </div>
                {isReq && <span className="sharp-badge text-primary border-primary/40 shrink-0">Requested</span>}
              </label>
            );
          })}
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <button data-testid="approve-cancel" onClick={onClose} className="h-10 px-5 border border-border font-semibold text-[13px]">Cancel</button>
          <button data-testid="approve-confirm" onClick={() => onConfirm(picked)} className="h-10 px-5 bg-primary text-primary-foreground font-semibold text-[13px]">
            Approve & notify
          </button>
        </div>
      </div>
    </div>
  );
}

function RejectDialog({ user, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white border border-border max-w-md w-full p-6">
        <div className="overline mb-2">Reject user</div>
        <div className="font-display font-black text-xl tracking-tight mb-1">{user.full_name}</div>
        <div className="text-[13px] text-muted-foreground mb-6">{user.email}</div>
        <label className="overline mb-2 block">Reason (optional)</label>
        <textarea data-testid="reject-reason-input" rows="3" value={reason} onChange={(e) => setReason(e.target.value)}
          className="w-full p-3 border border-border bg-white focus:outline-none focus:border-primary mb-6 text-[13.5px]" />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-5 border border-border font-semibold text-[13px]">Cancel</button>
          <button data-testid="reject-confirm" onClick={() => onConfirm(reason)} className="h-10 px-5 bg-destructive text-destructive-foreground font-semibold text-[13px]">
            Reject user
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PendingUsersPage() {
  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [approving, setApproving] = useState(null);
  const [rejecting, setRejecting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, m] = await Promise.all([
        api.get("/admin/users", { params: { status: "pending" } }),
        api.get("/admin/modules"),
      ]);
      setUsers(u.data);
      setModules(m.data);
    } catch (e) { setError(errorMessage(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (slugs) => {
    try {
      await api.post(`/admin/users/${approving.user_id}/approve`, { assigned_modules: slugs });
      setApproving(null);
      load();
    } catch (e) { setError(errorMessage(e)); }
  };

  const reject = async (reason) => {
    try {
      await api.post(`/admin/users/${rejecting.user_id}/reject`, { reason });
      setRejecting(null);
      load();
    } catch (e) { setError(errorMessage(e)); }
  };

  return (
    <AppShell section="admin">
      <div className="p-6 lg:p-10 max-w-6xl">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="overline mb-2">Admin Console</div>
            <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tight">Pending registrations</h1>
          </div>
          <button data-testid="pending-refresh" onClick={load} className="h-10 px-4 inline-flex items-center gap-2 border border-border font-semibold text-[13px] hover:bg-accent">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>

        {error && <div className="mb-5 p-3 border border-destructive/30 bg-destructive/5 text-destructive text-[13px]">{error}</div>}

        <div className="border border-border bg-white overflow-x-auto">
          {loading ? (
            <div className="p-8 overline">Loading...</div>
          ) : users.length === 0 ? (
            <div className="p-12 text-center" data-testid="pending-empty">
              <div className="font-display font-bold text-lg tracking-tight">All caught up.</div>
              <div className="text-[13px] text-muted-foreground mt-2">No users are waiting for approval.</div>
            </div>
          ) : (
            <table className="w-full text-[13.5px]">
              <thead>
                <tr className="bg-accent/60 text-[11px] uppercase tracking-wider text-muted-foreground">
                  <th className="text-left p-3 font-semibold">Name</th>
                  <th className="text-left p-3 font-semibold">Email</th>
                  <th className="text-left p-3 font-semibold">Requested</th>
                  <th className="text-left p-3 font-semibold">Organization</th>
                  <th className="text-left p-3 font-semibold">Method</th>
                  <th className="text-right p-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-t border-border" data-testid={`pending-row-${u.email}`}>
                    <td className="p-3 font-semibold">{u.full_name}</td>
                    <td className="p-3 text-muted-foreground">{u.email}</td>
                    <td className="p-3">
                      {u.requested_modules?.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {u.requested_modules.map((s) => (
                            <span key={s} className="sharp-badge text-primary border-primary/30">
                              {modules.find((m) => m.slug === s)?.name || s}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="p-3 text-muted-foreground">{u.organization || "-"}</td>
                    <td className="p-3"><span className="sharp-badge">{u.auth_provider}</span></td>
                    <td className="p-3 text-right">
                      <div className="inline-flex gap-2">
                        <button data-testid={`approve-button-${u.email}`} onClick={() => setApproving(u)} className="h-9 px-3 inline-flex items-center gap-1.5 bg-primary text-primary-foreground text-[12px] font-semibold hover:bg-[#001d6b]">
                          <Check className="h-3.5 w-3.5" /> Approve
                        </button>
                        <button data-testid={`reject-button-${u.email}`} onClick={() => setRejecting(u)} className="h-9 px-3 inline-flex items-center gap-1.5 border border-border text-[12px] font-semibold hover:bg-accent">
                          <X className="h-3.5 w-3.5" /> Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {approving && <ApproveDialog user={approving} modules={modules} onClose={() => setApproving(null)} onConfirm={approve} />}
      {rejecting && <RejectDialog user={rejecting} onClose={() => setRejecting(null)} onConfirm={reject} />}
    </AppShell>
  );
}
